import baseUrl from "@/base-url";

const PRESIGNED_URL_BUFFER_MS = 30 * 1000;
const STORAGE_KEY = "onekids.album.imageUrls.v1";
const MAX_CACHE_ENTRIES = 500;

function getStorage() {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch (error) {
    return null;
  }
}

function getTokenIdentity(token) {
  try {
    if (typeof token !== "string") return {};
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "=".repeat((4 - payload.length % 4) % 4);
    const claims = JSON.parse(window.atob(padded));
    // Legacy login uses sub for the user and jti for the active school/class/child.
    // These values only scope the cache; token validation remains on the backend.
    return {userId: claims.sub, loginId: claims.jti};
  } catch (error) {
    return {};
  }
}

class AlbumImageUrlCache {
  constructor() {
    this.entries = new Map();
    this.scope = undefined;
    this.generation = 0;
    if (typeof window !== "undefined") {
      window.addEventListener("storage", (event) => {
        if (event.key === "user" || event.key === null) this.ensureScope();
        if (event.key === STORAGE_KEY) {
          this.ensureScope();
          this.entries.clear();
          this.load();
        }
      });
    }
  }

  getScope() {
    try {
      const storage = getStorage();
      const user = storage && JSON.parse(storage.getItem("user"));
      const identity = getTokenIdentity(user && user.accessToken);
      const userId = user && (user.id != null ? user.id : identity.userId || user.username);
      if (userId == null || userId === "") return null;
      return JSON.stringify([
        baseUrl.url || "", "app_onekids", String(userId),
        String(user.schoolId != null ? user.schoolId : user.idSchool || ""),
        user.appType || "",
        String(identity.loginId != null ? identity.loginId : ""),
      ]);
    } catch (error) {
      return null;
    }
  }

  ensureScope() {
    const scope = this.getScope();
    if (scope === this.scope) return;
    this.scope = scope;
    this.generation += 1;
    this.entries.clear();
    this.load();
  }

  getContext() {
    this.ensureScope();
    return {scope: this.scope, generation: this.generation};
  }

  isCurrent(context) {
    this.ensureScope();
    return context.scope === this.scope && context.generation === this.generation;
  }

  load() {
    const storage = getStorage();
    if (!storage) return;
    try {
      const raw = storage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!this.scope || !saved || saved.scope !== this.scope || !Array.isArray(saved.entries)) {
        storage.removeItem(STORAGE_KEY);
        return;
      }
      saved.entries.forEach((entry) => {
        if (entry && typeof entry.objectKey === "string" && entry.objectKey &&
            typeof entry.url === "string" && entry.url && Number.isFinite(entry.expiresAt)) {
          this.entries.set(entry.objectKey, {url: entry.url, expiresAt: entry.expiresAt});
        }
      });
      if (this.prune() || this.entries.size !== saved.entries.length) this.persist();
    } catch (error) {
      // Invalid or unavailable storage must not prevent image loading.
      this.removeStoredCache();
    }
  }

  prune() {
    let changed = false;
    const minimumExpiry = Date.now() + PRESIGNED_URL_BUFFER_MS;
    this.entries.forEach((entry, objectKey) => {
      if (entry.expiresAt <= minimumExpiry) {
        this.entries.delete(objectKey);
        changed = true;
      }
    });
    while (this.entries.size > MAX_CACHE_ENTRIES) {
      this.entries.delete(this.entries.keys().next().value);
      changed = true;
    }
    return changed;
  }

  persist() {
    const storage = getStorage();
    if (!storage) return;
    try {
      if (!this.scope || this.entries.size === 0) {
        storage.removeItem(STORAGE_KEY);
        return;
      }
      const entries = Array.from(this.entries, ([objectKey, entry]) => ({objectKey, ...entry}));
      storage.setItem(STORAGE_KEY, JSON.stringify({scope: this.scope, entries}));
    } catch (error) {
      // Keep the memory cache usable when storage is full or blocked.
    }
  }

  get(objectKey) {
    this.ensureScope();
    if (this.prune()) this.persist();
    const entry = this.entries.get(objectKey);
    if (entry) {
      this.entries.delete(objectKey);
      this.entries.set(objectKey, entry);
    }
    return entry;
  }

  set(objectKey, entry, context) {
    // A signing response from before logout/school change must not refill the cache.
    if (!this.isCurrent(context)) return;
    this.entries.delete(objectKey);
    this.entries.set(objectKey, entry);
    this.prune();
    this.persist();
  }

  delete(objectKey) {
    this.ensureScope();
    if (this.entries.delete(objectKey)) this.persist();
  }

  removeStoredCache() {
    try {
      const storage = getStorage();
      if (storage) storage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Logout and cache invalidation still work in memory if storage is blocked.
    }
  }

  clear() {
    this.entries.clear();
    this.generation += 1;
    this.scope = this.getScope();
    this.removeStoredCache();
  }
}

export default new AlbumImageUrlCache();
